import { ApiCartItem, ApiOrderItem, ApiProduct } from '../types';

export function renderTemplate(template: string, data: Record<string, string | number>): string {
	const tokens = template.split(/\{\{|\}\}/);
	const buffer = tokens.map((token, i) => (i % 2 === 0 ? token : data[token]));
	return buffer.join('');
}

export function clone<T>(obj: T): T {
	return JSON.parse(JSON.stringify(obj));
}

export function sum(list: number[]): number {
	return list.reduce((a, b) => a + b, 0);
}

export const isMultiple = (a: number, b: number): boolean => b % a === 0;

export const debounce = (fn: (...params: any[]) => void, ms = 500): (() => void) => {
	let timeoutId: ReturnType<typeof setTimeout>;
	return function (this: any, ...args: any[]) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => fn.apply(this, args), ms);
	};
};

export const stripFacetCategory = (str: string | undefined): string => {
	if (!str) {
		return '';
	}
	const regex = /^([0-9]+)_([0-9]+)_/;
	return str.replace(regex, '');
};

const MAIN_CATEGORY_MAPPING: { [key in string]: number } = {
	'8112475': 1,
	'8112023': 2,
	'8112025': 3,
	'8112024': 4,
	'8112797': 5,
	'8112363': 6,
	'8112813': 7,
	'8112439': 8,
	'8113164': 1,
	'8113165': 2,
	'8113166': 3,
	'8113167': 4,
	'8113168': 5,
	'8113169': 6,
	'8113309': 7,
	// kgnl
	'8114131': 1,
	'8114132': 2,
	'8114133': 3,
	'8114135': 4,
	'8114136': 5,
	'8114137': 6,
	'8113134': 7,
};

export function getColorForCategory(categoryName: string): string {
	const [mainCategoryID, categoryID] = categoryName.split('_').slice(0, 2);
	const isMainCategory = mainCategoryID === categoryID;

	if (!mainCategoryID || !categoryID) {
		throw new Error(`Malformed category name: ${categoryName}`);
	}

	const mappedValue =
		Number(mainCategoryID) >= 1 && Number(mainCategoryID) <= 8
			? mainCategoryID
			: MAIN_CATEGORY_MAPPING[mainCategoryID];

	if (!mappedValue) {
		return '--color-grey';
	}

	return isMainCategory ? `--category-color-${mappedValue}` : `--category-color-${mappedValue}-light`;
}

export function orderItemToCartItem(item: ApiOrderItem): ApiCartItem {
	return {
		...item,
		price: { currentPrice: item.product.finalPrice },
		levels: true,
		tax: item.product.tax,
		product: {
			...item.product,
			image: item.image,
			unit: '',
			deliveryNotice: '',
			displayId: item.product.displayId + '',
		},
		stock: { quantity: item.stock, stocked: item.stock > 0 },
	};
}

export const filterDuplicateProducts = (products: ApiProduct[]) => {
	const seenIds = new Set();
	return products.filter((product) => {
		if (!seenIds.has(product.id)) {
			seenIds.add(product.id);
			return true;
		}
		return false;
	});
};

export const randomBool = () => Math.random() < 0.5;

export const WITH_CREDENTIALS = {
	withCredentials: true,
};

export const generateUUID = (): string => {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID();
	} else {
		// fallback for unsupported browsers
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0,
				v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}
};

export const filterEmptyValues = (payload: any) => Object.fromEntries(Object.entries(payload).filter(([_, v]) => !!v));

export const toAlphaNumeric = (str: string): string => str.replace(/[^a-zA-Z0-9]/g, '');

export const isEmpty = (obj: object) => Object.keys(obj).length === 0;

export const sortByIdOrder = <T extends { id: number | string }>(
	items: T[],
	orderedIds: Array<number | string>
): T[] => {
	if (!items?.length || !orderedIds?.length) return items;

	const orderMap = new Map<string, number>();
	for (let i = 0; i < orderedIds.length; i++) {
		orderMap.set(String(orderedIds[i]).trim(), i);
	}

	return items.sort((a, b) => {
		const aIndex = orderMap.get(String(a.id)) ?? Number.MAX_SAFE_INTEGER;
		const bIndex = orderMap.get(String(b.id)) ?? Number.MAX_SAFE_INTEGER;
		return aIndex - bIndex;
	});
};
